import { createContext, ReactNode, useMemo, useState } from "react";

import {
  IHospitalConfiguration,
  ILogin,
  ILoginResponse,
  IMfaDetails,
  ITenantDetails,
  IWardConfiguration,
} from "../@types/Login";
import { IPortalPreferences, IStaffPreferences } from "../@types/Preferences";
import { applyStoredLanguage } from "../config/i18n";
import {
  AUTH_GRAPH_PREFERENCES,
  AUTH_HOSPITAL_CONFIGURATION,
  AUTH_INTERCOM_DETAILS,
  AUTH_PORTAL_PREFERENCES,
  AUTH_SESSION_STARTED_AT,
  AUTH_STAFF_PREFERENCES,
  AUTH_TOKEN_STORAGE,
  AUTH_UNIT_PREFERENCES,
  AUTH_USER_DETAILS,
  AUTH_WARD_CONFIGURATION,
  AUTH_TENANT_DETAILS,
} from "../constants/localStorageKeys";
import { useToken } from "../hooks/useToken";
import { setApiBaseUrlForRegion } from "../services/api";
import { authService } from "../services/authService";
import { mixpanelActions } from "../utils/mixpanel";
import { safeJsonParse, safeStorage } from "../utils/storage";

type IAuthContextData = {
  authenticate: (credentials: ILogin) => Promise<ILoginResponse>;
  isAuthenticated: boolean;
  signOut: () => void;
  token: string | null;
  saveDataInLocalStorage: ({
    token,
    preferences,
  }: ILocalStorageData) => Promise<void>;
  loginDetails: ILogin | undefined;
  setLoginDetails: React.Dispatch<React.SetStateAction<ILogin | undefined>>;
  mfaDetails: IMfaDetails | undefined;
  setMfaDetails: React.Dispatch<React.SetStateAction<IMfaDetails | undefined>>;
};

type IAuthProviderProps = {
  children: ReactNode;
};

type ILocalStorageData = {
  token: any;
  preferences: any;
  portalPreferences: IPortalPreferences;
  intercomDetails: any;
  userDetails: any;
  hospitalConfiguration: IHospitalConfiguration;
  staffPreferences: IStaffPreferences;
  wardConfiguration: IWardConfiguration;
  tenantDetails: ITenantDetails;
};

export const AuthContext = createContext({} as IAuthContextData);

export function AuthProvider({ children }: IAuthProviderProps) {
  const { token, setToken } = useToken();
  const [loginDetails, setLoginDetails] = useState<ILogin>();
  const [mfaDetails, setMfaDetails] = useState<IMfaDetails>();

  async function saveDataInLocalStorage({
    token,
    preferences,
    portalPreferences,
    intercomDetails,
    userDetails,
    hospitalConfiguration,
    staffPreferences,
    wardConfiguration,
    tenantDetails,
  }: ILocalStorageData) {
    if (preferences) {
      safeStorage.setItem(AUTH_UNIT_PREFERENCES, JSON.stringify(preferences));

      // EN-5191: the language detector ran at boot, on the login screen, when there were no
      // preferences to read. Signing in is a client-side navigation, so nothing re-reads them
      // unless we ask — without this the whole session stays in the fallback language.
      // Awaited so the translations query is already keyed on the right language by the time
      // the authenticated routes mount — but never allowed to throw. Every write below this
      // point, the token included, would be skipped, leaving a half-written session that looks
      // like a successful login and is not one. A portal stuck in English is the better failure.
      try {
        await applyStoredLanguage();
      } catch (error) {
        console.error("Could not apply the stored language at sign-in", error);
      }
    }

    if (portalPreferences) {
      safeStorage.setItem(
        AUTH_PORTAL_PREFERENCES,
        JSON.stringify(portalPreferences)
      );
    }

    if (tenantDetails) {
      safeStorage.setItem(AUTH_TENANT_DETAILS, JSON.stringify(tenantDetails));
      await setApiBaseUrlForRegion(tenantDetails.region);
    }

    if (staffPreferences) {
      safeStorage.setItem(
        AUTH_STAFF_PREFERENCES,
        JSON.stringify(staffPreferences)
      );
    }

    if (hospitalConfiguration) {
      safeStorage.setItem(
        AUTH_HOSPITAL_CONFIGURATION,
        JSON.stringify(hospitalConfiguration)
      );
    }

    if (intercomDetails) {
      safeStorage.setItem(
        AUTH_INTERCOM_DETAILS,
        JSON.stringify(intercomDetails)
      );
    }

    if (userDetails) {
      mixpanelActions.identify(userDetails.userId);
      safeStorage.setItem(AUTH_USER_DETAILS, JSON.stringify(userDetails));
    }
    if (wardConfiguration) {
      safeStorage.setItem(
        AUTH_WARD_CONFIGURATION,
        JSON.stringify(wardConfiguration)
      );
    }
    if (token) {
      setToken(token);
      safeStorage.setItem(AUTH_SESSION_STARTED_AT, String(Date.now()));
    }
  }

  async function authenticate({ email, password }: ILogin) {
    const responseFromAPI = await authService.authenticate({ email, password });

    const {
      token,
      preferences,
      portalPreferences,
      mfaDetails,
      intercomDetails,
      userDetails,
      hospitalConfiguration,
      staffPreferences,
      wardConfiguration,
      tenantDetails,
    } = responseFromAPI;

    if (mfaDetails === null || mfaDetails === undefined)
      await saveDataInLocalStorage({
        token,
        preferences,
        portalPreferences,
        intercomDetails,
        userDetails,
        hospitalConfiguration,
        staffPreferences,
        wardConfiguration,
        tenantDetails,
      });

    return responseFromAPI;
  }

  function signOut() {
    const graphPreferences = safeStorage.getItem(AUTH_GRAPH_PREFERENCES);
    const tokenResponse = safeStorage.getItem(AUTH_TOKEN_STORAGE);
    const refreshToken = tokenResponse
      ? safeJsonParse<{ refreshToken?: string }>(tokenResponse, {}).refreshToken
      : undefined;

    // clear all local storage (token, etc) but keeps graph preferences like scale and hide acceptability filters.
    safeStorage.clear();
    mixpanelActions.reset();

    if (graphPreferences) {
      safeStorage.setItem(AUTH_GRAPH_PREFERENCES, graphPreferences);
    }

    // Fire-and-forget: revoke the refresh token server-side so a leaked token
    // (or the axios refresh interceptor on a lingering in-flight request)
    // cannot re-authenticate the session.
    if (refreshToken) {
      authService.signOutSession(refreshToken);
    }

    window.location.assign("/login");
  }

  const valuesToShare = useMemo(
    () => ({
      token,
      authenticate,
      signOut,
      isAuthenticated: Boolean(token),
      saveDataInLocalStorage,
      loginDetails,
      setLoginDetails,
      mfaDetails,
      setMfaDetails,
    }),
    [token, loginDetails, mfaDetails]
  );

  return (
    <AuthContext.Provider value={valuesToShare}>
      {children}
    </AuthContext.Provider>
  );
}
